import { Box, Button, TextField, Typography } from "@mui/material";
import React from "react";
import { CurrencyField } from "./CurrencyField";

export default function RepaymentInputs({
  interestOnly,
  termYears,
  interestRate,
  desiredMonthlyPayment,
  onChange,
  mode,
}: {
  interestOnly: boolean | undefined;
  termYears: number | undefined;
  interestRate: number | undefined;
  desiredMonthlyPayment: number | undefined;
  onChange: (changes: {
    termYears?: number;
    interestRate?: number;
    desiredMonthlyPayment?: number;
  }) => void;
  mode?: "termLength" | "interestRate" | "monthlyPayment";
}) {
  // In mode-specific forms, one field is managed separately (multiple values)
  // For the remaining two fields, user must choose one to specify
  let hasTerm: boolean;
  let hasRate: boolean;
  let hasPayment: boolean;
  let countSet: number;
  let termDisabled: boolean;
  let rateDisabled: boolean;
  let paymentDisabled: boolean;

  if (mode === "termLength") {
    // Term is managed separately, user must choose one of rate or payment
    hasTerm = true; // Always true since it's managed separately
    hasRate = (interestRate ?? 0) > 0;
    hasPayment = (desiredMonthlyPayment ?? 0) > 0;
    countSet = (hasRate ? 1 : 0) + (hasPayment ? 1 : 0);

    termDisabled = true; // Always disabled in termLength mode
    rateDisabled = countSet >= 1 && !hasRate;
    paymentDisabled = countSet >= 1 && !hasPayment;
  } else if (mode === "interestRate") {
    // Rate is managed separately, user must choose one of term or payment
    hasTerm = interestOnly || (termYears ?? 0) > 0;
    hasRate = true; // Always true since it's managed separately
    hasPayment = (desiredMonthlyPayment ?? 0) > 0;
    countSet = (hasTerm ? 1 : 0) + (hasPayment ? 1 : 0);

    termDisabled = countSet >= 1 && !hasTerm;
    rateDisabled = true; // Always disabled in interestRate mode
    paymentDisabled = countSet >= 1 && !hasPayment;
  } else if (mode === "monthlyPayment") {
    // Payment is managed separately, user must choose one of term or rate
    hasTerm = interestOnly || (termYears ?? 0) > 0;
    hasRate = (interestRate ?? 0) > 0;
    hasPayment = true; // Always true since it's managed separately
    countSet = (hasTerm ? 1 : 0) + (hasRate ? 1 : 0);

    termDisabled = countSet >= 1 && !hasTerm;
    rateDisabled = countSet >= 1 && !hasRate;
    paymentDisabled = true; // Always disabled in monthlyPayment mode
  } else {
    // Default mode: Two-of-three logic for Term, Interest Rate, Monthly Payment
    hasTerm = interestOnly || (termYears ?? 0) > 0; // Term required if Interest Only
    hasRate = (interestRate ?? 0) > 0;
    hasPayment = (desiredMonthlyPayment ?? 0) > 0;
    countSet = (hasTerm ? 1 : 0) + (hasRate ? 1 : 0) + (hasPayment ? 1 : 0);

    termDisabled = countSet >= 2 && !hasTerm;
    rateDisabled = countSet >= 2 && !hasRate;
    paymentDisabled = countSet >= 2 && !hasPayment;
  }

  let subtitle: string;
  if (mode === "termLength") {
    subtitle = "Repayment Inputs — choose one: Interest Rate, Monthly Payment";
  } else if (mode === "interestRate") {
    subtitle = interestOnly
      ? "Repayment Inputs — Term required"
      : "Repayment Inputs — choose one: Term, Monthly Payment";
  } else if (mode === "monthlyPayment") {
    subtitle = interestOnly
      ? "Repayment Inputs — Term required"
      : "Repayment Inputs — choose one: Term, Interest Rate";
  } else {
    subtitle = interestOnly
      ? "Repayment Inputs — Term required, choose one: Interest Rate, Monthly Payment"
      : "Repayment Inputs — choose any two: Term, Interest Rate, Monthly Payment";
  }

  const getValidationError = (field: string | null) => {
    if (mode === "termLength") {
      // User must provide either rate or payment
      const filledCount = [
        (interestRate ?? 0) > 0,
        (desiredMonthlyPayment ?? 0) > 0,
      ].filter(Boolean).length;
      return filledCount < 1
        ? "Either Interest Rate or Monthly Payment must be provided"
        : "";
    } else if (mode === "interestRate") {
      // User must provide either term or payment (term required if interestOnly)
      if (interestOnly) {
        return (termYears ?? 0) > 0 ? "" : "Term is required";
      }
      const filledCount = [
        (termYears ?? 0) > 0,
        (desiredMonthlyPayment ?? 0) > 0,
      ].filter(Boolean).length;
      return filledCount < 1
        ? "Either Term or Monthly Payment must be provided"
        : "";
    } else if (mode === "monthlyPayment") {
      // User must provide either term or rate (term required if interestOnly)
      if (interestOnly) {
        return (termYears ?? 0) > 0 ? "" : "Term is required";
      }
      const filledCount = [
        (termYears ?? 0) > 0,
        (interestRate ?? 0) > 0,
      ].filter(Boolean).length;
      return filledCount < 1
        ? "Either Term or Interest Rate must be provided"
        : "";
    } else {
      // Default mode: 2 of 3 required
      // If field is specified, never show an error if that field is filled
      if (field === "term" && (termYears ?? 0) > 0) {
        return "";
      }
      if (field === "rate" && (interestRate ?? 0) > 0) {
        return "";
      }
      const filledCount = [
        interestOnly || (termYears ?? 0) > 0,
        (interestRate ?? 0) > 0,
        (desiredMonthlyPayment ?? 0) > 0,
      ].filter(Boolean).length;
      return filledCount < 2 ? "At least 2 of 3 fields must be provided" : "";
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {subtitle}
      </Typography>
      {mode !== "termLength" && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            label="Mortgage Term (years)"
            type="number"
            slotProps={{ htmlInput: { min: 1, max: 83 } }}
            value={termYears ?? ""}
            onChange={(e) =>
              onChange({
                termYears:
                  e.target.value === ""
                    ? undefined
                    : parseInt(e.target.value, 10),
              })
            }
            fullWidth
            margin="normal"
            required={interestOnly}
            disabled={termDisabled}
            inputRef={(ref) => {
              if (ref) {
                ref.setCustomValidity(getValidationError("term"));
              }
            }}
          />
          {!interestOnly && (
            <Button
              type="button"
              size="small"
              onClick={() => onChange({ termYears: undefined })}
              disabled={!hasTerm}
              sx={{ whiteSpace: "nowrap" }}
            >
              Clear
            </Button>
          )}
        </Box>
      )}

      {mode !== "interestRate" &&
        !(mode === "monthlyPayment" && interestOnly) && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              label="Interest Rate (%)"
              type="number"
              slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
              value={interestRate ?? ""}
              onChange={(e) =>
                onChange({
                  interestRate:
                    e.target.value === ""
                      ? undefined
                      : parseFloat(e.target.value),
                })
              }
              fullWidth
              margin="normal"
              disabled={rateDisabled}
              inputRef={(ref) => {
                if (ref) {
                  ref.setCustomValidity(getValidationError("rate"));
                }
              }}
            />
            <Button
              type="button"
              size="small"
              onClick={() => onChange({ interestRate: undefined })}
              disabled={!hasRate}
              sx={{ whiteSpace: "nowrap" }}
            >
              Clear
            </Button>
          </Box>
        )}

      {mode !== "monthlyPayment" &&
        !(mode === "interestRate" && interestOnly) && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CurrencyField
              label="Monthly Payment"
              value={desiredMonthlyPayment}
              onChange={(val) =>
                onChange({
                  desiredMonthlyPayment: val === 0 ? undefined : val,
                })
              }
              fullWidth
              margin="normal"
              disabled={paymentDisabled}
              greaterThanZero
            />
            <Button
              type="button"
              size="small"
              onClick={() => onChange({ desiredMonthlyPayment: undefined })}
              disabled={!hasPayment}
              sx={{ whiteSpace: "nowrap" }}
            >
              Clear
            </Button>
          </Box>
        )}
    </Box>
  );
}
